import { motion, AnimatePresence } from "framer-motion";
import { OptionCard, JustificationPills } from "../shared";
import { MarkdownContent } from "@/components/ui/markdown-content";
import { cn } from "@/lib/utils";
import type {
  ChooseSignalsScenarioSpec,
  ChooseSignalsScenarioAnswer,
} from "@/types/hld-wizard";

const fadeIn = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.3 },
};

interface ChooseSignalsScenarioDecisionProps {
  spec: ChooseSignalsScenarioSpec;
  context: string;
  question: string;
  answer: ChooseSignalsScenarioAnswer | undefined;
  onChange: (answer: ChooseSignalsScenarioAnswer) => void;
  disabled?: boolean;
}

export function ChooseSignalsScenarioDecision({
  spec,
  context,
  question,
  answer,
  onChange,
  disabled,
}: ChooseSignalsScenarioDecisionProps) {
  const selectedOptionId = answer?.option_id ?? "";

  function selectOption(optionId: string) {
    onChange({
      option_id: optionId,
      signal_ids: [],
      scenario_option_id: "",
    });
  }

  function toggleSignal(id: string) {
    if (!answer) return;
    const current = answer.signal_ids;
    const next = current.includes(id)
      ? current.filter((s) => s !== id)
      : current.length < spec.signals.max_selections
        ? [...current, id]
        : current;
    onChange({ ...answer, signal_ids: next });
  }

  function selectScenario(optionId: string) {
    if (!answer) return;
    onChange({ ...answer, scenario_option_id: optionId });
  }

  // Convert signal items to pill format
  const signalPills = spec.signals.items.map((s) => ({
    id: s.id,
    text: s.text,
  }));

  return (
    <div className="space-y-6">
      {context && (
        <div className="border-l-2 border-primary/30 pl-4 text-muted-foreground">
          <MarkdownContent>{context}</MarkdownContent>
        </div>
      )}

      <MarkdownContent className="font-medium">{question}</MarkdownContent>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {spec.options.map((opt) => (
          <OptionCard
            key={opt.id}
            option={opt}
            selected={selectedOptionId === opt.id}
            onClick={() => selectOption(opt.id)}
            disabled={disabled}
          />
        ))}
      </div>

      {/* Signals selection */}
      <AnimatePresence>
        {selectedOptionId && (
          <motion.div {...fadeIn} className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">
              {spec.signals.question}
            </p>
            <JustificationPills
              items={signalPills}
              selected={answer?.signal_ids ?? []}
              onToggle={toggleSignal}
              max={spec.signals.max_selections}
              disabled={disabled}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scenario question */}
      <AnimatePresence>
        {answer &&
          answer.signal_ids.length === spec.signals.max_selections && (
            <motion.div {...fadeIn} className="space-y-4">
              <p className="font-medium">{spec.scenario.question}</p>
              <div className="space-y-2">
                {spec.scenario.options.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => selectScenario(opt.id)}
                    disabled={disabled}
                    className={cn(
                      "w-full rounded-lg border p-4 text-left transition-all",
                      answer.scenario_option_id === opt.id
                        ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                        : "border-border hover:border-primary/40",
                      disabled && "cursor-not-allowed opacity-50",
                    )}
                  >
                    <div className="font-semibold">{opt.text}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {opt.description}
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
}
